import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// YARD INSPECTOR — TOS easter egg, the Pac-Man variant.
// You're a yard inspector collecting manifests through a port yard. Four
// unauthorized vehicles patrol the corridors. Grab a supervisor override
// token to briefly ground them.
//
// Controls:
//   ← ↑ → ↓ / WASD     turn (queues your next intersection turn)
//   space              pause
//   R                  restart
//   M                  mute
//   ESC                close
//
// CUSTOMIZE: search for `// CUSTOMIZE:` to find tunable bits.

// ---- maze ----
const MAZE = [
  '############################',
  '#............##............#',
  '#.####.#####.##.#####.####.#',
  '#o####.#####.##.#####.####o#',
  '#.####.#####.##.#####.####.#',
  '#..........................#',
  '#.####.##.########.##.####.#',
  '#.####.##.########.##.####.#',
  '#......##....##....##......#',
  '######.##### ## #####.######',
  '######.##### ## #####.######',
  '######.##          ##.######',
  '######.## ###GG### ##.######',
  'T     .   #GGGGGG#   .     T',
  '######.## ######## ##.######',
  '######.##          ##.######',
  '######.## ######## ##.######',
  '#............##............#',
  '#.####.#####.##.#####.####.#',
  '#.####.#####.##.#####.####.#',
  '#o..##................##..o#',
  '###.##.##.########.##.##.###',
  '###.##.##.########.##.##.###',
  '#......##....##....##......#',
  '#.##########.##.##########.#',
  '#.##########.##.##########.#',
  '#.............P............#',
  '############################',
]

const MAZE_COLS = MAZE[0].length
const MAZE_ROWS = MAZE.length
const TILE = 22

// ---- gameplay ----
const PLAYER_SPEED = 2.0
const GHOST_SPEED = 1.85
const GHOST_FRIGHTENED_SPEED = 1.05
const GHOST_RETURN_SPEED = 3.4
const FRIGHTENED_DURATION_FRAMES = 60 * 7
const SCATTER_DURATION_FRAMES = 60 * 7
const CHASE_DURATION_FRAMES = 60 * 20
const PELLET_SCORE = 10
const POWER_SCORE = 50
const GHOST_SCORE_BASE = 200
const FRUIT_SCORE = 500
const FRUIT_SPAWN_AT = [70, 170]

// CUSTOMIZE: company name shown on game over.
const COMPANY_NAME = 'TERMINAL OS'

// ---- types ----
type GhostId = 'patrol' | 'ambush' | 'scout' | 'greenhorn'

interface GhostDef {
  id: GhostId
  color: string
  name: string
  homeIdx: number
}

const GHOSTS_DEF: GhostDef[] = [
  { id: 'patrol', color: '#d94a4a', name: 'PATROL', homeIdx: 0 },
  { id: 'ambush', color: '#d94a8f', name: 'AMBUSH', homeIdx: 1 },
  { id: 'scout', color: '#3a9bd6', name: 'SCOUT', homeIdx: 2 },
  { id: 'greenhorn', color: '#d97a2c', name: 'GREENHORN', homeIdx: 3 },
]

interface GridCell {
  col: number
  row: number
}

const SCATTER_CORNERS: GridCell[] = [
  { col: MAZE_COLS - 2, row: 1 },
  { col: 1, row: 1 },
  { col: MAZE_COLS - 1, row: MAZE_ROWS - 1 },
  { col: 0, row: MAZE_ROWS - 1 },
]

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
function manhattan(ax: number, ay: number, bx: number, by: number): number {
  return Math.abs(ax - bx) + Math.abs(ay - by)
}
function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx,
    dy = ay - by
  return dx * dx + dy * dy
}

type TileType = 'wall' | 'path' | 'depot' | 'tunnel'

interface ParsedMaze {
  tiles: TileType[][]
  playerSpawn: GridCell
  depotCells: GridCell[]
  pellets: Set<string>
  powers: Set<string>
  tunnels: GridCell[]
}

function parseMaze(rawMaze: string[]): ParsedMaze {
  const tiles: TileType[][] = []
  let playerSpawn: GridCell = { col: 1, row: 1 }
  const depotCells: GridCell[] = []
  const pellets = new Set<string>()
  const powers = new Set<string>()
  const tunnels: GridCell[] = []
  for (let r = 0; r < rawMaze.length; r++) {
    const row: TileType[] = []
    for (let c = 0; c < rawMaze[r].length; c++) {
      const ch = rawMaze[r][c]
      if (ch === '#') row.push('wall')
      else if (ch === '.') {
        row.push('path')
        pellets.add(`${c},${r}`)
      } else if (ch === 'o') {
        row.push('path')
        powers.add(`${c},${r}`)
      } else if (ch === '-' || ch === ' ') row.push('path')
      else if (ch === 'G') {
        row.push('depot')
        depotCells.push({ col: c, row: r })
      } else if (ch === 'P') {
        row.push('path')
        playerSpawn = { col: c, row: r }
      } else if (ch === 'T') {
        row.push('tunnel')
        tunnels.push({ col: c, row: r })
      } else row.push('path')
    }
    tiles.push(row)
  }
  return { tiles, playerSpawn, depotCells, pellets, powers, tunnels }
}

function tileAt(tiles: TileType[][], col: number, row: number): TileType {
  if (row < 0 || row >= tiles.length) return 'wall'
  if (col < 0 || col >= tiles[0].length) return 'wall'
  return tiles[row][col]
}
function isWalkable(tiles: TileType[][], col: number, row: number): boolean {
  const t = tileAt(tiles, col, row)
  return t === 'path' || t === 'tunnel' || t === 'depot'
}
function isPlayerWalkable(tiles: TileType[][], col: number, row: number): boolean {
  const t = tileAt(tiles, col, row)
  return t === 'path' || t === 'tunnel'
}

// ---- AUDIO ----
type WindowWithWebkitAudio = Window & {
  webkitAudioContext?: typeof AudioContext
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false

  init() {
    if (this.ctx) return
    try {
      const Ctor = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.22
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // audio unavailable — fail silently
    }
  }
  private env(node: GainNode, attack: number, decay: number, peak = 1) {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }
  munch(toggle: boolean) {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.value = toggle ? 440 : 330
    this.env(gain, 0.001, 0.05, 0.1)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.06)
  }
  power() {
    if (!this.ctx || !this.masterGain || this.muted) return
    ;[330, 440, 660, 880].forEach((freq, i) => {
      if (!this.ctx || !this.masterGain) return
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      osc.type = 'square'
      osc.frequency.value = freq
      const t = this.ctx.currentTime + i * 0.05
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.18, t + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1)
      osc.connect(gain).connect(this.masterGain)
      osc.start(t)
      osc.stop(t + 0.12)
    })
  }
  eatGhost() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(220, this.ctx.currentTime + 0.25)
    this.env(gain, 0.005, 0.25, 0.3)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.27)
  }
  fruit() {
    if (!this.ctx || !this.masterGain || this.muted) return
    ;[880, 1320, 1760].forEach((freq, i) => {
      if (!this.ctx || !this.masterGain) return
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const t = this.ctx.currentTime + i * 0.05
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.2, t + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15)
      osc.connect(gain).connect(this.masterGain)
      osc.start(t)
      osc.stop(t + 0.17)
    })
  }
  death() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(660, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(80, this.ctx.currentTime + 0.9)
    this.env(gain, 0.005, 0.9, 0.3)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.95)
  }
  win() {
    if (!this.ctx || !this.masterGain || this.muted) return
    ;[523, 659, 784, 1047].forEach((freq, i) => {
      if (!this.ctx || !this.masterGain) return
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      osc.type = 'triangle'
      osc.frequency.value = freq
      const t = this.ctx.currentTime + i * 0.12
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.18, t + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3)
      osc.connect(gain).connect(this.masterGain)
      osc.start(t)
      osc.stop(t + 0.32)
    })
  }
  toggleMute() {
    this.muted = !this.muted
  }
}

// ---- entity types ----
interface Direction {
  dx: number
  dy: number
}

interface Player {
  col: number
  row: number
  dir: Direction
  nextDir: Direction
  munchToggle: boolean
  deathFrames: number
  mouthAnim: number
}

type GhostState = 'in_depot' | 'exiting' | 'alive' | 'frightened' | 'returning'

interface Ghost {
  id: GhostId
  color: string
  name: string
  homeIdx: number
  col: number
  row: number
  dir: Direction
  state: GhostState
  releaseTimer: number
  animOffset: number
}

interface Floater {
  x: number
  y: number
  text: string
  color: string
  life: number
}

interface Fruit {
  col: number
  row: number
  life: number
}

type GameStatus = 'ready' | 'playing' | 'paused' | 'gameover' | 'levelclear'
type GameMode = 'scatter' | 'chase'

interface GameState {
  tiles: TileType[][]
  playerSpawn: GridCell
  depotCells: GridCell[]
  pellets: Set<string>
  powers: Set<string>
  tunnels: GridCell[]
  player: Player
  ghosts: Ghost[]
  score: number
  hi: number
  lives: number
  level: number
  status: GameStatus
  modeTimer: number
  mode: GameMode
  modeIndex: number
  frightenedTimer: number
  ghostChain: number
  pelletsEaten: number
  fruit: Fruit | null
  fruitSpawnedFlags: boolean[]
  time: number
  floaters: Floater[]
  shake: number
  introFrames: number
}

interface HudState {
  score: number
  hi: number
  lives: number
  level: number
  status: GameStatus
  muted: boolean
}

// ---- entity factories ----
function makePlayer(spawn: GridCell): Player {
  return {
    col: spawn.col,
    row: spawn.row,
    dir: { dx: -1, dy: 0 },
    nextDir: { dx: -1, dy: 0 },
    munchToggle: false,
    deathFrames: 0,
    mouthAnim: 0,
  }
}

function makeGhosts(depotCells: GridCell[]): Ghost[] {
  const ghosts: Ghost[] = []
  for (let i = 0; i < GHOSTS_DEF.length; i++) {
    const def = GHOSTS_DEF[i]
    const homeCell = depotCells[i % depotCells.length] || depotCells[0] || { col: 14, row: 12 }
    ghosts.push({
      ...def,
      col: homeCell.col,
      row: homeCell.row,
      dir: { dx: 0, dy: -1 },
      state: i === 0 ? 'exiting' : 'in_depot',
      releaseTimer: i * 180,
      animOffset: i * 0.5,
    })
  }
  return ghosts
}

interface YardPacmanProps {
  onClose?: () => void
}

const YardPacman: FC<YardPacmanProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const keysRef = useRef<Record<string, boolean>>({})
  const audioRef = useRef<GameAudio>(new GameAudio())
  const startGameRef = useRef<(() => void) | null>(null)
  const [hud, setHud] = useState<HudState>({
    score: 0,
    hi: 0,
    lives: 3,
    level: 1,
    status: 'ready',
    muted: false,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    let savedHi = 0
    try {
      savedHi = parseInt(localStorage.getItem('yardInspectorHi') || '0', 10) || 0
    } catch {
      // localStorage unavailable
    }

    const initState = (): GameState => {
      const parsed = parseMaze(MAZE)
      return {
        ...parsed,
        player: makePlayer(parsed.playerSpawn),
        ghosts: makeGhosts(parsed.depotCells),
        score: 0,
        hi: savedHi,
        lives: 3,
        level: 1,
        status: 'ready',
        modeTimer: SCATTER_DURATION_FRAMES,
        mode: 'scatter',
        modeIndex: 0,
        frightenedTimer: 0,
        ghostChain: 0,
        pelletsEaten: 0,
        fruit: null,
        fruitSpawnedFlags: [false, false],
        time: 0,
        floaters: [],
        shake: 0,
        introFrames: 90,
      }
    }

    stateRef.current = initState()

    const startGame = () => {
      const s = initState()
      s.status = 'playing'
      stateRef.current = s
      setHud({
        score: 0,
        hi: s.hi,
        lives: 3,
        level: 1,
        status: 'playing',
        muted: audio.muted,
      })
    }
    startGameRef.current = startGame

    const respawnAfterDeath = () => {
      const s = stateRef.current
      if (!s) return
      s.player = makePlayer(s.playerSpawn)
      s.ghosts = makeGhosts(s.depotCells)
      s.modeTimer = SCATTER_DURATION_FRAMES
      s.mode = 'scatter'
      s.modeIndex = 0
      s.frightenedTimer = 0
      s.ghostChain = 0
      s.introFrames = 90
    }

    const queueDir = (dx: number, dy: number) => {
      const s = stateRef.current
      if (!s || s.status !== 'playing' || s.player.deathFrames > 0 || s.introFrames > 0) return
      s.player.nextDir = { dx, dy }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      audio.init()
      const k = e.key.toLowerCase()
      keysRef.current[e.key] = true
      keysRef.current[k] = true
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key))
        e.preventDefault()
      if (e.key === 'Escape' && onClose) onClose()
      const s = stateRef.current
      if (!s) return
      if (
        s.status === 'ready' &&
        (e.key === ' ' || /^Arrow/.test(e.key) || /^[wasd]$/i.test(e.key))
      ) {
        startGame()
        return
      }
      if (e.key === 'ArrowLeft' || k === 'a') queueDir(-1, 0)
      else if (e.key === 'ArrowRight' || k === 'd') queueDir(1, 0)
      else if (e.key === 'ArrowUp' || k === 'w') queueDir(0, -1)
      else if (e.key === 'ArrowDown' || k === 's') queueDir(0, 1)
      if (e.key === ' ' && s.status === 'playing') {
        s.status = 'paused'
        setHud(p => ({ ...p, status: 'paused' }))
      } else if (e.key === ' ' && s.status === 'paused') {
        s.status = 'playing'
        setHud(p => ({ ...p, status: 'playing' }))
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
      if (e.key === 'r' || e.key === 'R') {
        const cur = s.status
        if (cur === 'gameover' || cur === 'ready') startGame()
        else if (cur === 'playing' || cur === 'paused') {
          if (window.confirm('Restart current shift? Your progress will be lost.')) startGame()
        }
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      keysRef.current[e.key] = false
      keysRef.current[e.key.toLowerCase()] = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    const showFloater = (col: number, row: number, text: string, color: string) => {
      const s = stateRef.current
      if (!s) return
      s.floaters.push({
        x: col * TILE + TILE / 2,
        y: row * TILE + TILE / 2,
        text,
        color,
        life: 60,
      })
    }

    interface Movable {
      col: number
      row: number
      dir: Direction
      nextDir?: Direction
    }

    const moveEntity = (
      e: Movable,
      speed: number,
      walkableFn: (tiles: TileType[][], col: number, row: number) => boolean,
    ): boolean => {
      const s = stateRef.current
      if (!s) return false
      const tiles = s.tiles
      const onCenterX = Math.abs(e.col - Math.round(e.col)) < 0.05
      const onCenterY = Math.abs(e.row - Math.round(e.row)) < 0.05
      if (onCenterX && onCenterY) {
        e.col = Math.round(e.col)
        e.row = Math.round(e.row)
        if (e.nextDir && (e.nextDir.dx !== e.dir.dx || e.nextDir.dy !== e.dir.dy)) {
          const nc = e.col + e.nextDir.dx
          const nr = e.row + e.nextDir.dy
          if (walkableFn(tiles, nc, nr)) {
            e.dir = { ...e.nextDir }
          }
        }
        const tc = e.col + e.dir.dx
        const tr = e.row + e.dir.dy
        if (!walkableFn(tiles, tc, tr)) return false
      }
      e.col += e.dir.dx * speed
      e.row += e.dir.dy * speed
      if (e.col < -0.5) e.col = MAZE_COLS - 0.5
      else if (e.col > MAZE_COLS - 0.5) e.col = -0.5
      return true
    }

    const ghostTile = (g: Ghost): GridCell => ({
      col: Math.round(g.col),
      row: Math.round(g.row),
    })

    const pickGhostDir = (g: Ghost, targetCol: number, targetRow: number): Direction => {
      const s = stateRef.current
      if (!s) return g.dir
      const tiles = s.tiles
      const { col, row } = ghostTile(g)
      const dirs: Direction[] = [
        { dx: 0, dy: -1 },
        { dx: -1, dy: 0 },
        { dx: 0, dy: 1 },
        { dx: 1, dy: 0 },
      ]
      let best: Direction | null = null
      let bestDist = Infinity
      for (const d of dirs) {
        if (d.dx === -g.dir.dx && d.dy === -g.dir.dy) continue
        const nc = col + d.dx,
          nr = row + d.dy
        if (!isWalkable(tiles, nc, nr)) continue
        const dd = dist2(nc, nr, targetCol, targetRow)
        if (dd < bestDist) {
          bestDist = dd
          best = d
        }
      }
      return best || g.dir
    }

    const ghostTarget = (g: Ghost, player: Player): GridCell => {
      const s = stateRef.current
      if (!s) return { col: 0, row: 0 }
      const pcol = Math.round(player.col),
        prow = Math.round(player.row)
      if (s.frightenedTimer > 0 && g.state === 'alive') {
        return { col: pcol, row: prow }
      }
      if (g.state === 'returning') {
        const home = s.depotCells[0] || {
          col: Math.floor(MAZE_COLS / 2),
          row: Math.floor(MAZE_ROWS / 2),
        }
        return { col: home.col, row: home.row }
      }
      if (s.mode === 'scatter') {
        return SCATTER_CORNERS[g.homeIdx] || { col: 1, row: 1 }
      }
      if (g.id === 'patrol') {
        return { col: pcol, row: prow }
      }
      if (g.id === 'ambush') {
        return { col: pcol + player.dir.dx * 4, row: prow + player.dir.dy * 4 }
      }
      if (g.id === 'scout') {
        const patrol = s.ghosts.find(x => x.id === 'patrol')
        const aheadCol = pcol + player.dir.dx * 2
        const aheadRow = prow + player.dir.dy * 2
        if (!patrol) return { col: aheadCol, row: aheadRow }
        const pCol = Math.round(patrol.col),
          pRow = Math.round(patrol.row)
        return { col: aheadCol + (aheadCol - pCol), row: aheadRow + (aheadRow - pRow) }
      }
      if (g.id === 'greenhorn') {
        const d = manhattan(g.col, g.row, pcol, prow)
        if (d > 8) return { col: pcol, row: prow }
        return SCATTER_CORNERS[g.homeIdx]
      }
      return { col: pcol, row: prow }
    }

    const randomWalkableDir = (g: Ghost): Direction => {
      const s = stateRef.current
      if (!s) return g.dir
      const tiles = s.tiles
      const { col, row } = ghostTile(g)
      const dirs: Direction[] = [
        { dx: 0, dy: -1 },
        { dx: -1, dy: 0 },
        { dx: 0, dy: 1 },
        { dx: 1, dy: 0 },
      ]
        .filter(d => !(d.dx === -g.dir.dx && d.dy === -g.dir.dy))
        .filter(d => isWalkable(tiles, col + d.dx, row + d.dy))
      if (dirs.length === 0) return g.dir
      return dirs[Math.floor(Math.random() * dirs.length)]
    }

    let animationId = 0

    const draw = () => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      s.time++

      const screenW = canvas.width / dpr
      const screenH = canvas.height / dpr
      const mazeW = MAZE_COLS * TILE
      const mazeH = MAZE_ROWS * TILE
      const availW = screenW - 40
      const availH = screenH - 120
      const scale = Math.min(availW / mazeW, availH / mazeH, 1.6)
      const offsetX = (screenW - mazeW * scale) / 2
      const offsetY = (screenH - mazeH * scale) / 2 + 20

      let shakeX = 0,
        shakeY = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        shakeY = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr)

      ctx.fillStyle = '#080e18'
      ctx.fillRect(0, 0, screenW, screenH)

      ctx.save()
      ctx.translate(offsetX, offsetY)
      ctx.scale(scale, scale)

      drawMaze(ctx, s.tiles)

      for (const key of s.pellets) {
        const [c, r] = key.split(',').map(Number)
        drawPellet(ctx, c, r)
      }
      for (const key of s.powers) {
        const [c, r] = key.split(',').map(Number)
        drawPower(ctx, c, r, s.time)
      }
      if (s.fruit) drawFruit(ctx, s.fruit.col, s.fruit.row, s.time)

      // ---- update game ----
      if (s.status === 'playing' && s.player.deathFrames === 0) {
        if (s.introFrames > 0) {
          s.introFrames--
        } else {
          if (s.frightenedTimer === 0) {
            s.modeTimer--
            if (s.modeTimer <= 0) {
              if (s.mode === 'scatter') {
                s.mode = 'chase'
                s.modeTimer = CHASE_DURATION_FRAMES
              } else {
                s.mode = 'scatter'
                s.modeTimer = SCATTER_DURATION_FRAMES
              }
              for (const g of s.ghosts) {
                if (g.state === 'alive') g.dir = { dx: -g.dir.dx, dy: -g.dir.dy }
              }
            }
          } else {
            s.frightenedTimer--
            if (s.frightenedTimer === 0) {
              s.ghostChain = 0
            }
          }

          moveEntity(s.player, PLAYER_SPEED / TILE, isPlayerWalkable)
          s.player.mouthAnim += 0.25

          for (const g of s.ghosts) {
            if (g.state === 'in_depot') {
              g.releaseTimer--
              if (g.releaseTimer <= 0) g.state = 'exiting'
              continue
            }
            if (g.state === 'exiting') {
              g.row -= GHOST_SPEED / TILE
              g.col += (Math.floor(MAZE_COLS / 2) - g.col) * 0.1
              if (g.row <= s.depotCells[0].row - 2) {
                g.state = s.frightenedTimer > 0 ? 'frightened' : 'alive'
                g.col = Math.round(g.col)
                g.row = Math.round(g.row)
                g.dir = { dx: -1, dy: 0 }
              }
              continue
            }
            const onCenterX = Math.abs(g.col - Math.round(g.col)) < 0.05
            const onCenterY = Math.abs(g.row - Math.round(g.row)) < 0.05
            if (onCenterX && onCenterY) {
              g.col = Math.round(g.col)
              g.row = Math.round(g.row)
              if (s.frightenedTimer > 0 && g.state !== 'returning') {
                g.dir = randomWalkableDir(g)
              } else {
                const t = ghostTarget(g, s.player)
                g.dir = pickGhostDir(g, t.col, t.row)
              }
              if (g.state === 'returning') {
                const home = s.depotCells[0]
                if (Math.abs(g.col - home.col) <= 1 && Math.abs(g.row - home.row) <= 1) {
                  g.state = 'alive'
                  g.dir = { dx: 0, dy: -1 }
                  g.col = home.col
                  g.row = home.row - 2
                }
              }
            }
            let sp = GHOST_SPEED / TILE
            if (s.frightenedTimer > 0 && g.state !== 'returning') sp = GHOST_FRIGHTENED_SPEED / TILE
            if (g.state === 'returning') sp = GHOST_RETURN_SPEED / TILE
            g.col += g.dir.dx * sp
            g.row += g.dir.dy * sp
            if (g.col < -0.5) g.col = MAZE_COLS - 0.5
            else if (g.col > MAZE_COLS - 0.5) g.col = -0.5
          }

          const pcol = Math.round(s.player.col)
          const prow = Math.round(s.player.row)
          if (Math.abs(s.player.col - pcol) < 0.4 && Math.abs(s.player.row - prow) < 0.4) {
            const key = `${pcol},${prow}`
            if (s.pellets.has(key)) {
              s.pellets.delete(key)
              s.score += PELLET_SCORE
              s.pelletsEaten++
              s.player.munchToggle = !s.player.munchToggle
              audio.munch(s.player.munchToggle)
              for (let i = 0; i < FRUIT_SPAWN_AT.length; i++) {
                if (!s.fruitSpawnedFlags[i] && s.pelletsEaten >= FRUIT_SPAWN_AT[i]) {
                  s.fruitSpawnedFlags[i] = true
                  s.fruit = { col: s.playerSpawn.col, row: s.playerSpawn.row - 6, life: 60 * 10 }
                }
              }
            } else if (s.powers.has(key)) {
              s.powers.delete(key)
              s.score += POWER_SCORE
              s.frightenedTimer = FRIGHTENED_DURATION_FRAMES
              s.ghostChain = 0
              audio.power()
              for (const g of s.ghosts) {
                if (g.state === 'alive') g.dir = { dx: -g.dir.dx, dy: -g.dir.dy }
              }
            } else if (s.fruit && s.fruit.col === pcol && s.fruit.row === prow) {
              s.score += FRUIT_SCORE
              showFloater(pcol, prow, `+${FRUIT_SCORE} TEU`, '#ffd166')
              audio.fruit()
              s.fruit = null
            }
          }

          for (const g of s.ghosts) {
            if (g.state === 'in_depot' || g.state === 'exiting' || g.state === 'returning') continue
            const d2 = dist2(g.col, g.row, s.player.col, s.player.row)
            if (d2 < 0.6 * 0.6) {
              if (s.frightenedTimer > 0) {
                const points = GHOST_SCORE_BASE * Math.pow(2, s.ghostChain)
                s.score += points
                s.ghostChain++
                showFloater(g.col, g.row, `+${points}`, '#3a9bd6')
                audio.eatGhost()
                g.state = 'returning'
                g.col = Math.round(g.col)
                g.row = Math.round(g.row)
              } else {
                s.player.deathFrames = 90
                s.lives--
                audio.death()
                s.shake = 12
                if (s.lives < 0) {
                  s.status = 'gameover'
                  if (s.score > s.hi) {
                    s.hi = s.score
                    try {
                      localStorage.setItem('yardInspectorHi', String(s.hi))
                    } catch {
                      // localStorage unavailable
                    }
                  }
                  setHud(p => ({ ...p, status: 'gameover', hi: s.hi }))
                }
                break
              }
            }
          }

          if (s.fruit) {
            s.fruit.life--
            if (s.fruit.life <= 0) s.fruit = null
          }

          if (s.pellets.size === 0 && s.powers.size === 0) {
            s.status = 'levelclear'
            s.shake = 0
            audio.win()
            setTimeout(() => {
              const cur = stateRef.current
              if (!cur || cur.status !== 'levelclear') return
              const parsed = parseMaze(MAZE)
              cur.tiles = parsed.tiles
              cur.pellets = parsed.pellets
              cur.powers = parsed.powers
              cur.depotCells = parsed.depotCells
              cur.tunnels = parsed.tunnels
              cur.player = makePlayer(parsed.playerSpawn)
              cur.ghosts = makeGhosts(parsed.depotCells)
              cur.level++
              cur.modeTimer = SCATTER_DURATION_FRAMES
              cur.mode = 'scatter'
              cur.frightenedTimer = 0
              cur.ghostChain = 0
              cur.pelletsEaten = 0
              cur.fruit = null
              cur.fruitSpawnedFlags = [false, false]
              cur.introFrames = 90
              cur.status = 'playing'
              setHud(p => ({ ...p, level: cur.level, status: 'playing' }))
            }, 1500)
          }
        }
      } else if (s.status === 'playing' && s.player.deathFrames > 0) {
        s.player.deathFrames--
        if (s.player.deathFrames === 0 && s.status === 'playing') {
          if (s.lives < 0) {
            s.status = 'gameover'
          } else {
            respawnAfterDeath()
          }
        }
      }

      s.floaters = s.floaters.filter(f => {
        f.y -= 0.4
        f.life--
        return f.life > 0
      })

      for (const g of s.ghosts) {
        drawGhost(ctx, g, s.frightenedTimer > 0, s.frightenedTimer, s.time)
      }

      drawPlayer(ctx, s.player, s.time)

      s.floaters.forEach(f => {
        const alpha = Math.min(1, f.life / 30)
        ctx.fillStyle = f.color
        ctx.globalAlpha = alpha
        ctx.font = 'bold 12px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(f.text, f.x, f.y)
      })
      ctx.globalAlpha = 1

      if (s.introFrames > 0 && s.status === 'playing') {
        ctx.fillStyle = '#ffd166'
        ctx.font = 'bold 20px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText('READY', (MAZE_COLS * TILE) / 2, (s.playerSpawn.row - 3) * TILE)
      }

      ctx.restore()

      setHud(prev => {
        if (
          prev.score === s.score &&
          prev.lives === s.lives &&
          prev.level === s.level &&
          prev.status === s.status
        )
          return prev
        return { ...prev, score: s.score, lives: s.lives, level: s.level, status: s.status }
      })

      const vignette = ctx.createRadialGradient(
        screenW / 2,
        screenH / 2,
        screenH * 0.4,
        screenW / 2,
        screenH / 2,
        screenH,
      )
      vignette.addColorStop(0, 'rgba(0,0,0,0)')
      vignette.addColorStop(1, 'rgba(0,0,0,0.5)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, screenW, screenH)

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('resize', resize)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SCORE</span>
          <span style={styles.hudValue}>{String(hud.score).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BEST</span>
          <span style={styles.hudValue}>{String(hud.hi).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SHIFT</span>
          <span style={styles.hudValue}>{String(hud.level).padStart(2, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>LIVES</span>
          <span style={styles.hudValue}>{'◉ '.repeat(Math.max(0, hud.lives))}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        <button
          style={styles.restart}
          onClick={() => {
            if (hud.status === 'playing' || hud.status === 'paused') {
              if (!window.confirm('Restart current shift? Your progress will be lost.')) return
            }
            audioRef.current.init()
            startGameRef.current?.()
          }}
          onMouseDown={e => e.preventDefault()}
          title='Restart shift (R)'
        >
          ↻ RESTART
        </button>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {hud.status === 'ready' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>YARD INSPECTOR</div>
          <div style={styles.overlaySubLarge}>collect manifests · avoid unauthorized vehicles</div>
          <div style={styles.legend}>
            <div style={styles.legendItem}>
              <span style={{ color: '#d94a4a' }}>●</span> PATROL — direct chaser
            </div>
            <div style={styles.legendItem}>
              <span style={{ color: '#d94a8f' }}>●</span> AMBUSH — cuts you off
            </div>
            <div style={styles.legendItem}>
              <span style={{ color: '#3a9bd6' }}>●</span> SCOUT — flanks you
            </div>
            <div style={styles.legendItem}>
              <span style={{ color: '#d97a2c' }}>●</span> GREENHORN — wanders erratically
            </div>
          </div>
          <div style={styles.controls}>
            <span style={{ color: '#cfd8e8' }}>
              ← ↑ → ↓ / WASD steer &nbsp;·&nbsp; SPACE pause &nbsp;·&nbsp; R restart
            </span>
            <br />
            <span style={{ color: '#5a6478' }}>
              grab a glowing override token to ground vehicles for a few seconds
            </span>
          </div>
          <div style={{ ...styles.controls, marginTop: 18 }}>press any movement key to start</div>
        </div>
      )}

      {hud.status === 'paused' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>YARD HOLD</div>
          <div style={styles.overlaySub}>press SPACE to resume</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>SHIFT TERMINATED</div>
          <div style={styles.overlaySub}>
            final score: {hud.score.toLocaleString()}{' '}
            {hud.score >= hud.hi && hud.score > 0 ? '· NEW BEST' : ''}
          </div>
          <pre style={styles.report}>
            {`SHIFT REPORT — ${COMPANY_NAME}
inspector down at shift ${hud.level}
${hud.score.toLocaleString()} TEU manifests filed
${hud.score >= hud.hi && hud.score > 0 ? 'operator commendation issued' : 'see you next shift'}`}
          </pre>
          <div style={styles.overlaySub}>press R or click RESTART to try again · ESC to close</div>
        </div>
      )}
    </div>
  )
}

export default YardPacman

// ============= DRAWING =============

function drawMaze(ctx: CanvasRenderingContext2D, tiles: TileType[][]) {
  for (let r = 0; r < tiles.length; r++) {
    for (let c = 0; c < tiles[0].length; c++) {
      if (tiles[r][c] === 'wall') {
        drawWallCell(ctx, c, r)
      } else if (tiles[r][c] === 'depot') {
        drawDepotCell(ctx, c, r)
      } else if (tiles[r][c] === 'tunnel') {
        drawTunnelCell(ctx, c, r)
      }
    }
  }
}

function drawWallCell(ctx: CanvasRenderingContext2D, c: number, r: number) {
  const x = c * TILE
  const y = r * TILE
  ctx.fillStyle = '#0d1622'
  ctx.fillRect(x, y, TILE, TILE)
  const colorIdx = (Math.floor(c / 3) + Math.floor(r / 3)) % 6
  const colors = ['#2d6da3', '#a85e22', '#2d7a45', '#a8923a', '#a83a72', '#923030']
  ctx.fillStyle = colors[colorIdx]
  ctx.fillRect(x + 2, y + 2, TILE - 4, TILE - 4)
  ctx.fillStyle = 'rgba(255,255,255,0.18)'
  ctx.fillRect(x + 2, y + 2, TILE - 4, 2)
  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.fillRect(x + 2, y + TILE - 4, TILE - 4, 2)
  ctx.strokeStyle = 'rgba(0,0,0,0.2)'
  ctx.lineWidth = 0.5
  for (let i = 1; i < 4; i++) {
    const lx = x + (TILE / 4) * i
    ctx.beginPath()
    ctx.moveTo(lx, y + 4)
    ctx.lineTo(lx, y + TILE - 4)
    ctx.stroke()
  }
}

function drawDepotCell(ctx: CanvasRenderingContext2D, c: number, r: number) {
  const x = c * TILE
  const y = r * TILE
  ctx.fillStyle = '#1a2233'
  ctx.fillRect(x, y, TILE, TILE)
  ctx.fillStyle = 'rgba(255, 209, 102, 0.08)'
  ctx.fillRect(x, y, TILE, TILE)
}

function drawTunnelCell(ctx: CanvasRenderingContext2D, c: number, r: number) {
  const x = c * TILE
  const y = r * TILE
  ctx.fillStyle = '#0d1622'
  ctx.fillRect(x, y, TILE, TILE)
  ctx.strokeStyle = 'rgba(120, 130, 150, 0.4)'
  ctx.lineWidth = 0.6
  for (let i = 0; i < 4; i++) {
    ctx.beginPath()
    ctx.moveTo(x + i * 6, y)
    ctx.lineTo(x + i * 6 + 3, y + TILE)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(x + i * 6, y + TILE)
    ctx.lineTo(x + i * 6 + 3, y)
    ctx.stroke()
  }
}

function drawPellet(ctx: CanvasRenderingContext2D, c: number, r: number) {
  const cx = c * TILE + TILE / 2
  const cy = r * TILE + TILE / 2
  ctx.fillStyle = '#cfd8e8'
  ctx.fillRect(cx - 2, cy - 2, 4, 4)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
  ctx.fillRect(cx - 1, cy - 1, 3, 0.5)
}

function drawPower(ctx: CanvasRenderingContext2D, c: number, r: number, time: number) {
  const cx = c * TILE + TILE / 2
  const cy = r * TILE + TILE / 2
  const pulse = 0.5 + Math.sin(time * 0.2) * 0.5
  ctx.fillStyle = `rgba(255, 209, 102, ${0.2 + pulse * 0.2})`
  ctx.beginPath()
  ctx.arc(cx, cy, 8, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#ffd166'
  ctx.beginPath()
  ctx.arc(cx, cy, 5, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#1a1a1a'
  ctx.font = 'bold 7px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('S', cx, cy + 0.5)
  ctx.textBaseline = 'alphabetic'
}

function drawFruit(ctx: CanvasRenderingContext2D, c: number, r: number, time: number) {
  const cx = c * TILE + TILE / 2
  const cy = r * TILE + TILE / 2
  const bob = Math.sin(time * 0.1) * 1.5
  ctx.fillStyle = 'rgba(201, 127, 74, 0.25)'
  ctx.beginPath()
  ctx.arc(cx, cy + bob, 12, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#3a2620'
  ctx.fillRect(cx - 5, cy - 6 + bob, 10, 12)
  ctx.fillStyle = '#2a1b16'
  ctx.fillRect(cx - 5, cy - 6 + bob, 10, 2)
  ctx.fillStyle = '#c97f4a'
  ctx.fillRect(cx - 5, cy - 1 + bob, 10, 4)
  ctx.strokeStyle = 'rgba(220, 230, 245, 0.5)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(cx, cy - 8 + bob)
  ctx.quadraticCurveTo(cx + 2, cy - 12 + bob, cx, cy - 16 + bob)
  ctx.stroke()
}

function drawPlayer(ctx: CanvasRenderingContext2D, p: Player, time: number) {
  const cx = p.col * TILE + TILE / 2
  const cy = p.row * TILE + TILE / 2

  if (p.deathFrames > 0) {
    const t = 1 - p.deathFrames / 90
    ctx.fillStyle = '#ffd166'
    ctx.globalAlpha = 1 - t
    ctx.beginPath()
    ctx.arc(cx, cy, TILE * 0.45 * (1 - t * 0.7), 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1
    return
  }

  ctx.save()
  ctx.translate(cx, cy)
  const angle = Math.atan2(p.dir.dy, p.dir.dx)
  ctx.rotate(angle)

  ctx.fillStyle = 'rgba(0,0,0,0.4)'
  ctx.fillRect(-TILE * 0.4, -TILE * 0.35, TILE * 0.8, TILE * 0.7)

  const bodyGrad = ctx.createLinearGradient(0, -TILE * 0.35, 0, TILE * 0.35)
  bodyGrad.addColorStop(0, '#e8b347')
  bodyGrad.addColorStop(0.5, '#c9943a')
  bodyGrad.addColorStop(1, '#8a6422')
  ctx.fillStyle = bodyGrad
  ctx.fillRect(-TILE * 0.4, -TILE * 0.35, TILE * 0.8, TILE * 0.7)
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(-TILE * 0.4, -TILE * 0.35, TILE * 0.8, 1.5)
  ctx.fillRect(-TILE * 0.4, TILE * 0.32, TILE * 0.8, 1.5)

  ctx.fillStyle = '#1a2236'
  ctx.fillRect(TILE * 0.05, -TILE * 0.25, TILE * 0.3, TILE * 0.5)
  ctx.fillStyle = '#3a5a8a'
  ctx.fillRect(TILE * 0.1, -TILE * 0.2, TILE * 0.2, TILE * 0.4)
  ctx.fillStyle = 'rgba(255,255,255,0.3)'
  ctx.fillRect(TILE * 0.1, -TILE * 0.2, TILE * 0.08, 1)

  ctx.fillStyle = '#5a5a5a'
  const forkLen = TILE * 0.35
  ctx.fillRect(TILE * 0.4, -TILE * 0.18, forkLen, 2)
  ctx.fillRect(TILE * 0.4, TILE * 0.16, forkLen, 2)

  ctx.fillStyle = `rgba(255, 209, 102, ${0.5 + Math.sin(time * 0.3) * 0.5})`
  ctx.fillRect(-TILE * 0.05, -TILE * 0.05, 2, 2)

  ctx.restore()
}

function drawGhost(
  ctx: CanvasRenderingContext2D,
  g: Ghost,
  frightened: boolean,
  frightTimer: number,
  time: number,
) {
  const cx = g.col * TILE + TILE / 2
  const cy = g.row * TILE + TILE / 2

  let bodyColor = g.color
  const isReturning = g.state === 'returning'
  if (frightened && !isReturning) {
    if (frightTimer < 60 * 2 && Math.floor(time / 6) % 2 === 0) {
      bodyColor = '#cfd8e8'
    } else {
      bodyColor = '#3a4458'
    }
  }
  if (isReturning) bodyColor = '#1a2236'

  ctx.fillStyle = 'rgba(0,0,0,0.3)'
  ctx.beginPath()
  ctx.ellipse(cx, cy + TILE * 0.32, TILE * 0.32, 2, 0, 0, Math.PI * 2)
  ctx.fill()

  ctx.save()
  ctx.translate(cx, cy)

  if (g.id === 'patrol') {
    drawTruckBody(ctx, bodyColor, TILE * 0.85, TILE * 0.65)
    ctx.fillStyle = `rgba(220, 60, 60, ${0.4 + Math.sin(time * 0.4) * 0.6})`
    ctx.fillRect(-TILE * 0.18, -TILE * 0.32, 3, 3)
    ctx.fillRect(TILE * 0.15, -TILE * 0.32, 3, 3)
  } else if (g.id === 'ambush') {
    drawTruckBody(ctx, bodyColor, TILE * 0.7, TILE * 0.7)
    ctx.fillStyle = 'rgba(255,255,255,0.3)'
    ctx.fillRect(-TILE * 0.3, -2, TILE * 0.6, 4)
  } else if (g.id === 'scout') {
    ctx.fillStyle = bodyColor
    ctx.beginPath()
    ctx.arc(0, 0, TILE * 0.3, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = 'rgba(0,0,0,0.5)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.arc(0, 0, TILE * 0.3, 0, Math.PI * 2)
    ctx.stroke()
    const rotorAngle = time * 0.5 + g.animOffset
    ctx.strokeStyle = 'rgba(180,180,180,0.7)'
    ctx.lineWidth = 1.5
    ;[0, Math.PI / 2, Math.PI, Math.PI * 1.5].forEach(a => {
      const rx = Math.cos(a) * TILE * 0.3
      const ry = Math.sin(a) * TILE * 0.3
      ctx.beginPath()
      ctx.moveTo(rx + Math.cos(rotorAngle) * 5, ry + Math.sin(rotorAngle) * 5)
      ctx.lineTo(rx - Math.cos(rotorAngle) * 5, ry - Math.sin(rotorAngle) * 5)
      ctx.stroke()
    })
    ctx.fillStyle = '#1a1a1a'
    ctx.beginPath()
    ctx.arc(0, 0, 3, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#d94a4a'
    ctx.beginPath()
    ctx.arc(0, 0, 1.5, 0, Math.PI * 2)
    ctx.fill()
  } else {
    const wobble = Math.sin(time * 0.2 + g.animOffset) * 0.2
    ctx.rotate(wobble)
    drawTruckBody(ctx, bodyColor, TILE * 0.65, TILE * 0.55)
    ctx.fillStyle = '#ffd166'
    ctx.font = 'bold 9px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('?', 0, -TILE * 0.18)
  }

  if (!frightened || isReturning) {
    const eyeOffsetX = g.dir.dx * 2
    const eyeOffsetY = g.dir.dy * 2
    ctx.fillStyle = '#cfd8e8'
    ctx.fillRect(-5, -2, 4, 4)
    ctx.fillRect(1, -2, 4, 4)
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(-5 + eyeOffsetX, -2 + eyeOffsetY, 2, 2)
    ctx.fillRect(1 + eyeOffsetX, -2 + eyeOffsetY, 2, 2)
  } else {
    ctx.fillStyle = '#ffd166'
    ctx.fillRect(-3, -1, 2, 2)
    ctx.fillRect(1, -1, 2, 2)
    ctx.strokeStyle = '#ffd166'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(-4, 4)
    ctx.lineTo(-2, 2)
    ctx.lineTo(0, 4)
    ctx.lineTo(2, 2)
    ctx.lineTo(4, 4)
    ctx.stroke()
  }

  ctx.restore()
}

function drawTruckBody(ctx: CanvasRenderingContext2D, color: string, w: number, h: number) {
  ctx.fillStyle = color
  ctx.fillRect(-w / 2, -h / 2, w, h)
  ctx.fillStyle = 'rgba(255,255,255,0.18)'
  ctx.fillRect(-w / 2, -h / 2, w, 1.5)
  ctx.fillStyle = 'rgba(0,0,0,0.4)'
  ctx.fillRect(-w / 2, h / 2 - 1.5, w, 1.5)
  ctx.strokeStyle = 'rgba(0,0,0,0.5)'
  ctx.lineWidth = 0.8
  ctx.strokeRect(-w / 2, -h / 2, w, h)
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: { width: '100%', height: '100%', display: 'block', touchAction: 'none' },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 28,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  restart: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    background: 'transparent',
    border: '1px solid #4a3a18',
    color: '#ffd166',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  close: {
    marginLeft: 8,
    pointerEvents: 'auto',
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: 'rgba(8, 14, 24, 0.7)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  overlayTitle: {
    fontSize: 36,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  overlaySubLarge: { fontSize: 13, letterSpacing: '0.22em', color: '#cfd8e8', marginBottom: 4 },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 11,
    letterSpacing: '0.14em',
    color: '#cfd8e8',
    marginTop: 6,
  },
  legendItem: { display: 'flex', gap: 8, alignItems: 'center' },
  controls: {
    marginTop: 8,
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#7a8398',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  report: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
    maxWidth: 460,
  },
}
